import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, X, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * IntroStyleVideoPlayer — iter166
 *
 * Verbatim clone of the IntroVideoPlayer in HomePage.js (lines 22-155).
 * Used for the onboarding training videos so they look and behave
 * IDENTICAL to "Watch the intro video":
 *
 *   • `fixed inset-0 bg-black z-[100]` — fills the entire screen.
 *   • Autoplays on mount (no muted-autoplay overlay).
 *   • Pause/play + close buttons top-right.
 *   • Countdown timer top-left while playing.
 *   • Linear-watch enforcement (snap back if user scrubs ahead).
 *   • "Step Complete!" celebration with Continue button on end.
 *
 * Props let callers customize copy/labels; the markup is identical.
 */
const IntroStyleVideoPlayer = ({
  src,
  completionTitle = 'Step Complete!',
  completionBody = 'Great job! Continue with your onboarding.',
  closeWarning = 'Complete the video to mark this step as done',
  onComplete,
  onClose,
  testId = 'intro-style-video',
}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [maxWatchedTime, setMaxWatchedTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (videoRef.current) {
      const p = videoRef.current.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
      setIsPlaying(true);
    }
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleVideoEnd = () => {
    setVideoEnded(true);
    setIsPlaying(false);
    if (typeof onComplete === 'function') onComplete();
  };

  const handleClose = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    if (!videoEnded) {
      toast.info(closeWarning);
    }
    if (typeof onClose === 'function') onClose();
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      setCurrentTime(video.currentTime);
      if (video.currentTime > maxWatchedTime) {
        setMaxWatchedTime(video.currentTime);
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeeking = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      if (video.currentTime > maxWatchedTime + 0.5) {
        video.currentTime = maxWatchedTime;
        toast.warning('Please watch the video without skipping');
      }
    }
  };

  const remainingTime = Math.max(0, duration - currentTime);

  return (
    <div className="fixed inset-0 bg-black z-[100] flex items-center justify-center" data-testid={testId}>
      <div className="relative w-full max-w-md mx-4">
        <div className="absolute top-4 right-4 z-10 flex gap-2">
          <Button
            variant="ghost"
            className="bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 h-auto"
            onClick={handlePause}
            data-testid={`${testId}-playpause`}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            className="bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 h-auto"
            onClick={handleClose}
            data-testid={`${testId}-close`}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Countdown Timer */}
        {duration > 0 && !videoEnded && (
          <div className="absolute top-4 left-4 z-10 bg-black/60 px-3 py-1 rounded-full">
            <span className="text-white text-sm font-medium">
              {formatTime(remainingTime)} remaining
            </span>
          </div>
        )}

        <video
          ref={videoRef}
          className="w-full rounded-lg"
          onEnded={handleVideoEnd}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onSeeking={handleSeeking}
          playsInline
        >
          <source src={src} type="video/mp4" />
        </video>

        {videoEnded && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded-lg">
            <div className="text-center text-white">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">{completionTitle}</h3>
              <p className="text-gray-300 mb-4">{completionBody}</p>
              <Button onClick={handleClose} className="bg-[#757150] hover:bg-[#8a8560]" data-testid={`${testId}-continue`}>
                Continue
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntroStyleVideoPlayer;
